import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import {
    BookingApplicationCheckoutState,
    BookingCheckoutState,
    Offer,
    SelectedAddOns,
    SelectedOffers,
} from '@shared/types';
import styles from './styles.module.scss';
import { StepContainer } from '../../../StepContainer';
import { OffersSelect } from '../../../OffersSelect';
import { AddonsSection } from '../../../AddonsSection';
import { BookingCheckoutContext } from '../../../CheckoutContext/BookingContext';
import { BookingApplicationCheckoutContext } from '../../../CheckoutContext/BookingApplicationContext';
import { VouchersSection } from '../../../VoucherSection';
import { useCheckoutContext } from '../../../CheckoutContext';
import { isOfferDateValid } from '../../../shared/isOfferDateValid';

export type PriceStepProps = BookingCheckoutContext | BookingApplicationCheckoutContext;

const parseSelectedOffersFromState = (
    checkoutState: BookingCheckoutState | BookingApplicationCheckoutState,
    offers: Offer[]
): SelectedOffers => {
    if (!checkoutState.stepState.price.state?.offers) {
        const firstOfferIsValid = isOfferDateValid(offers[0]);
        if (firstOfferIsValid) {
            return { [offers[0]._id]: 1 };
        }
        return {};
    }
    return checkoutState.stepState.price.state.offers.reduce((initialState, selectedOffer) => {
        return { ...initialState, [selectedOffer.offerId]: selectedOffer.quantity };
    }, {});
};

const parseSelectedAddOnsFromState = (
    checkoutState: BookingCheckoutState | BookingApplicationCheckoutState
): SelectedAddOns => {
    if (!checkoutState.stepState.price.state?.addOns) {
        return {};
    }
    return checkoutState.stepState.price.state.addOns.reduce((initialState, selectedAddon) => {
        return { ...initialState, [selectedAddon.addonId]: selectedAddon.quantity };
    }, {});
};

export const PriceStep: FunctionComponent<PriceStepProps> = ({
    course,
    educator,
    checkoutState,
    submitResponse,
    addons,
    offers,
    offersById,
    selectedOccurrence,
}) => {
    const hidePricing = useMemo(
        () => checkoutState.origin !== 'marketplace' && educator.feature_flags?.hide_pricing,
        [checkoutState.origin, educator.feature_flags]
    );
    const initialOffersState = useMemo(() => {
        return parseSelectedOffersFromState(checkoutState, offers);
    }, [checkoutState, offers]);

    const initialAddOnState = useMemo(() => {
        return parseSelectedAddOnsFromState(checkoutState);
    }, [checkoutState]);

    const [selectedOffers, setSelectedOffers] = useState<SelectedOffers>(initialOffersState);
    const [selectedAddOns, setSelectedAddOns] = useState<SelectedAddOns>(initialAddOnState);

    const { isLoading } = useCheckoutContext();
    const isWaitingOnOccurrenceLoading = useMemo(() => {
        return isLoading === true && selectedOccurrence === null;
    }, [isLoading, selectedOccurrence]);
    useEffect(() => {
        if (isWaitingOnOccurrenceLoading === false) {
            setSelectedOffers(initialOffersState);
            setSelectedAddOns(initialAddOnState);
        }
    }, [isWaitingOnOccurrenceLoading, initialOffersState, initialAddOnState]);

    const [selectedVoucher, setSelectedVoucher] = useState<string | null>(
        checkoutState.stepState.price.state?.voucherId || null
    );
    const [selectedVoucherCode, setSelectedVoucherCode] = useState<string | null>(null);
    const onVoucherSelected = useCallback((voucherId: string, voucherCode: string) => {
        setSelectedVoucher(voucherId);
        setSelectedVoucherCode(voucherCode);
    }, []);

    const payloadSelectedOffers = useMemo(() => {
        return Object.entries(selectedOffers).map(([offerId, quantity]) => {
            return { offerId, quantity };
        });
    }, [selectedOffers]);
    const payloadSelectedAddOns = useMemo(() => {
        return Object.entries(selectedAddOns).map(([addonId, quantity]) => {
            return { addonId, quantity };
        });
    }, [selectedAddOns]);

    const onContinue = useCallback(() => {
        submitResponse({
            actionType: 'pricing options picked',
            payload: {
                selectedOffers: payloadSelectedOffers,
                selectedAddons: payloadSelectedAddOns,
                appliedPromoCode: selectedVoucherCode || undefined,
            },
        });
    }, [submitResponse, payloadSelectedOffers, payloadSelectedAddOns, selectedVoucherCode]);

    const onBackClick = useCallback(() => {
        return submitResponse({ actionType: 'go back', payload: {} });
    }, [submitResponse]);

    const totalSpacesUsed = useMemo(() => {
        return Object.entries(selectedOffers).reduce((total, [offerId, quantity]) => {
            return total + quantity * (offersById[offerId]?.spaces || 1);
        }, 0);
    }, [offersById, selectedOffers]);

    const totalAvailableSpaces = useMemo(() => {
        return (
            selectedOccurrence?.available_spaces || selectedOccurrence?.spaces || course.spaces || 0
        );
    }, [course, selectedOccurrence]);

    const availableSpaces = useMemo(() => {
        return totalAvailableSpaces - totalSpacesUsed;
    }, [totalAvailableSpaces, totalSpacesUsed]);

    const optimisticCheckoutState = useMemo(():
        | BookingCheckoutState
        | BookingApplicationCheckoutState => {
        const priceState:
            | BookingCheckoutState['stepState']['price']
            | BookingApplicationCheckoutState['stepState']['price'] = {
            status: 'complete',
            state: {
                addOns: payloadSelectedAddOns,
                offers: payloadSelectedOffers,
                spacesRequested: totalSpacesUsed,
                voucherId: selectedVoucher || undefined,
            },
        };

        if (checkoutState.flow === 'booking') {
            return {
                ...checkoutState,
                stepState: {
                    ...checkoutState.stepState,
                    price: priceState,
                },
            };
        } else {
            return {
                ...checkoutState,
                stepState: {
                    ...checkoutState.stepState,
                    price: priceState,
                },
            };
        }
    }, [
        checkoutState,
        payloadSelectedOffers,
        payloadSelectedAddOns,
        totalSpacesUsed,
        selectedVoucher,
    ]);

    return (
        <StepContainer
            course={course}
            optimisticCheckoutState={optimisticCheckoutState}
            onContinueClick={onContinue}
            onBackClick={onBackClick}
        >
            <div className={styles.container} style={isLoading ? { filter: 'blur(2px)' } : {}}>
                <h1>Select Spaces</h1>
                <OffersSelect
                    offers={isWaitingOnOccurrenceLoading ? [] : offers}
                    availableSpaces={availableSpaces}
                    selectedOffers={selectedOffers}
                    setSelectedOffers={setSelectedOffers}
                    hidePricing={hidePricing}
                    currency={course.currency}
                />
                <AddonsSection
                    addons={isWaitingOnOccurrenceLoading ? [] : addons}
                    selectedAddOns={selectedAddOns}
                    setSelectedAddOns={setSelectedAddOns}
                    hidePricing={hidePricing}
                    currency={course.currency}
                />
                <VouchersSection
                    offers={isWaitingOnOccurrenceLoading ? [] : offers}
                    courseId={course._id}
                    educatorId={course.educator_ref}
                    onVoucherSelected={onVoucherSelected}
                    origin={checkoutState.origin}
                />
            </div>
        </StepContainer>
    );
};

export default PriceStep;
